<script setup lang="ts">
import { ref } from "vue";
import { useI18n } from "vue-i18n";

import { masterApi } from "@/entities/master";
import { mapMasterError } from "@/features/master-profile";
import type { AppError } from "@/shared/api";
import { toast } from "@/shared/lib";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormAlert,
} from "@/shared/ui";

/** Небезпечна зона — видалення майстра. Widget-internal (не shared/ui). */
const props = defineProps<{ masterId: string; masterName: string }>();

const emit = defineEmits<{
  deleted: [id: string];
}>();

const { t } = useI18n();

type DeleteState = "idle" | "confirming" | "deleting" | "blocked";

const deleteState = ref<DeleteState>("idle");

function requestDelete(): void {
  deleteState.value = "confirming";
}

function cancelDelete(): void {
  deleteState.value = "idle";
}

function onOpenChange(open: boolean): void {
  if (!open && deleteState.value !== "deleting") {
    cancelDelete();
  }
}

async function confirmDelete(): Promise<void> {
  if (deleteState.value === "deleting") {
    return;
  }
  deleteState.value = "deleting";
  try {
    await masterApi.deleteMaster(props.masterId);
    deleteState.value = "idle";
    emit("deleted", props.masterId);
  } catch (e) {
    const mapped = mapMasterError(e as AppError, "delete");
    if (mapped.surface === "deleted") {
      deleteState.value = "idle";
      emit("deleted", props.masterId);
      return;
    }
    if (mapped.surface === "blocked") {
      deleteState.value = "blocked";
      return;
    }
    toast.error(t(mapped.i18nKey));
    deleteState.value = "confirming";
  }
}
</script>

<template>
  <div>
    <Card class="border-destructive/30">
      <CardHeader>
        <CardTitle>{{ t("masters.sections.dangerZone") }}</CardTitle>
      </CardHeader>
      <CardContent class="flex flex-col gap-3">
        <p class="text-sm text-muted-foreground">
          {{ t("masters.dangerZone.description") }}
        </p>
        <div>
          <Button
            variant="destructive"
            data-testid="danger-delete-btn"
            @click="requestDelete"
          >
            {{ t("masters.actions.delete") }}
          </Button>
        </div>
      </CardContent>
    </Card>

    <AlertDialog
      :open="deleteState !== 'idle'"
      @update:open="onOpenChange"
    >
      <AlertDialogContent data-testid="danger-delete-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{{ t("masters.delete.title") }}</AlertDialogTitle>
          <AlertDialogDescription>
            {{ t("masters.delete.confirm", { name: masterName }) }}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <FormAlert
          v-if="deleteState === 'blocked'"
          data-testid="danger-delete-blocked"
          variant="destructive"
          :message="t('masters.delete.blocked')"
        />

        <AlertDialogFooter>
          <AlertDialogCancel>{{ t("masters.actions.cancel") }}</AlertDialogCancel>
          <Button
            v-if="deleteState !== 'blocked'"
            data-testid="danger-delete-confirm"
            variant="destructive"
            :loading="deleteState === 'deleting'"
            @click="confirmDelete"
          >
            {{ t("masters.actions.delete") }}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>
</template>
